import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { FaUserEdit, FaSort, FaSortUp, FaSortDown, FaTrash } from "react-icons/fa";
import ManageClientForm from "./ManageClientForm";
import Navbar from "../components/AgentNavbar";

export default function ManageClients() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const LOGGED_IN_AGENT_USERNAME = localStorage.getItem("currentUser") || "";

  useEffect(() => {
    const unsubscribeClients = onSnapshot(collection(db, "users"), (snapshot) => {
      let clientList = snapshot.docs
        .filter((doc) => doc.data().role === "client")
        .map((doc) => ({ id: doc.id, ...doc.data() }));

      // Superuser "buksna" sees all clients, others see only their own
      if (LOGGED_IN_AGENT_USERNAME !== "buksna") {
        clientList = clientList.filter(
          (client) => client.createdBy === LOGGED_IN_AGENT_USERNAME
        );
      }

      setClients(clientList);
    });

    return () => unsubscribeClients();
  }, [LOGGED_IN_AGENT_USERNAME]);

  // Robust parser for the variety of joined formats in the DB
  const parseJoinedToEpoch = (value) => {
    if (!value && value !== 0) return NaN;

    // Firestore Timestamp-like objects or Date instances
    if (typeof value === "object") {
      // Firestore Timestamp
      try {
        if (typeof value.toDate === "function") {
          const t = value.toDate().getTime();
          return isNaN(t) ? NaN : t;
        }
      } catch (e) {
        // ignore and continue
      }

      // Raw seconds field
      if ("seconds" in value && typeof value.seconds === "number") {
        const t = value.seconds * 1000;
        return isNaN(t) ? NaN : t;
      }

      if (value instanceof Date) {
        const t = value.getTime();
        return isNaN(t) ? NaN : t;
      }
    }

    // Numeric epoch (seconds or ms)
    if (typeof value === "number") {
      // heuristic: if < 1e12 assume seconds
      if (value < 1e12) return value * 1000;
      return value;
    }

    // Strings - try robust parsing
    if (typeof value === "string") {
      let s = value.trim();

      // Common problem: some entries contain " at " (e.g., "Aug 19, 2025 at 12:20 AM")
      // Normalize " at " to a single space so regex/Date parsing works better
      s = s.replace(/\bat\b/ig, " ");

      // collapse multiple spaces
      s = s.replace(/\s{2,}/g, " ").trim();

      // Try manual regex parse for patterns like:
      // "Aug 19, 2025, 12:20 AM" | "Aug 19, 2025 12:20 AM" | "August 19 2025 12:20 AM"
      const regex = /([A-Za-z]{3,9})\s+(\d{1,2}),?\s+(\d{4})(?:[,\s]+(\d{1,2}):(\d{2})\s*(AM|PM|am|pm))?/;
      const m = s.match(regex);
      if (m) {
        const monthName = m[1];
        const day = parseInt(m[2], 10);
        const year = parseInt(m[3], 10);
        const hourStr = m[4];
        const minuteStr = m[5];
        const ampm = m[6] ? m[6].toLowerCase() : null;

        let hour = hourStr ? parseInt(hourStr, 10) : 0;
        const minute = minuteStr ? parseInt(minuteStr, 10) : 0;

        if (ampm) {
          if (ampm === "pm" && hour !== 12) hour = hour + 12;
          if (ampm === "am" && hour === 12) hour = 0;
        }

        const monKey = monthName.slice(0, 3).toLowerCase();
        const monthMap = {
          jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
          jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
        };
        const monthIndex = monthMap[monKey];
        if (monthIndex !== undefined) {
          const d = new Date(year, monthIndex, day, hour, minute);
          if (!isNaN(d.getTime())) return d.getTime();
        }
      }

      // Fallback attempts: try Date.parse on normalized strings
      const try1 = Date.parse(s);
      if (!isNaN(try1)) return try1;

      // Remove commas and try again (some environments parse better without commas)
      const try2 = Date.parse(s.replace(/,/g, ""));
      if (!isNaN(try2)) return try2;

      // As a last fallback, attempt to extract ISO-like numbers (YYYY-MM-DD HH:MM)
      const isoLike = s.replace(/([A-Za-z]+)/g, "").replace(/[^\d:\s-]/g, " ").trim();
      const try3 = Date.parse(isoLike);
      if (!isNaN(try3)) return try3;
    }

    return NaN;
  };

  useEffect(() => {
    let tempClients = [...clients];

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      tempClients = tempClients.filter((c) =>
        [
          c.id,
          c.fullName,
          c.email,
          c.currentBalance,
          c.status,
          c.feePaid ? "Yes" : "No",
          c.joined,
          c.createdBy,
        ].some(
          (field) =>
            field !== undefined &&
            field !== null &&
            String(field).toLowerCase().includes(query)
        )
      );
    }

    if (sortConfig.key) {
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      tempClients.sort((a, b) => {
        // Special handling for joined (dates) with robust parsing
        if (sortConfig.key === "joined") {
          const aParsed = parseJoinedToEpoch(a.joined);
          const bParsed = parseJoinedToEpoch(b.joined);

          // Push invalid/missing dates to the bottom regardless of direction
          const aIsInvalid = isNaN(aParsed);
          const bIsInvalid = isNaN(bParsed);
          if (aIsInvalid && bIsInvalid) return 0;
          if (aIsInvalid) return 1;
          if (bIsInvalid) return -1;

          if (aParsed === bParsed) return 0;
          return dir * (aParsed - bParsed);
        }

        const aValue = a[sortConfig.key] ?? "";
        const bValue = b[sortConfig.key] ?? "";

        if (typeof aValue === "string" || typeof bValue === "string") {
          return dir === 1
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        } else if (typeof aValue === "number" || typeof bValue === "number") {
          const aNum = Number(aValue) || 0;
          const bNum = Number(bValue) || 0;
          return dir === 1 ? aNum - bNum : bNum - aNum;
        } else {
          return 0;
        }
      });
    }

    setFilteredClients(tempClients);
  }, [clients, searchQuery, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: "4px" }} />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp style={{ marginLeft: "4px" }} />
    ) : (
      <FaSortDown style={{ marginLeft: "4px" }} />
    );
  };

  const handleDelete = async (clientId) => {
    const confirmed = window.confirm("Are you sure you want to delete this client?");
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "users", clientId));
    } catch (err) {
      console.error("Error deleting client:", err);
      alert("Failed to delete client.");
    }
  };

  return (
    <div style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Navbar />
      <div
        style={{
          width: "95%",
          maxWidth: "1400px",
          margin: "2rem auto",
          padding: "2rem",
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          fontFamily: "sans-serif",
        }}
      >
        <h2 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" }}>
          Manage Clients
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "1.5rem", fontSize: "0.9rem" }}>
          View, search, and manage your assigned clients.
        </p>

        <input
          type="text"
          placeholder="Search clients across all fields..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "0.6rem 1rem",
            marginBottom: "1.5rem",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            fontSize: "0.9rem",
          }}
        />

        <div style={{ overflowX: "auto", width: "100%" }}>
          <table
            style={{
              width: "100%",
              minWidth: "900px",
              borderCollapse: "collapse",
              fontSize: "0.85rem",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f3f4f6", textAlign: "left" }}>
                <th style={thStyle} onClick={() => requestSort("id")}>
                  Client ID {getSortIcon("id")}
                </th>
                <th style={thStyle} onClick={() => requestSort("fullName")}>
                  Name {getSortIcon("fullName")}
                </th>
                <th style={thStyle} onClick={() => requestSort("email")}>
                  Email {getSortIcon("email")}
                </th>
                <th style={thStyle} onClick={() => requestSort("currentBalance")}>
                  Balance {getSortIcon("currentBalance")}
                </th>
                <th style={thStyle} onClick={() => requestSort("status")}>
                  Status {getSortIcon("status")}
                </th>
                <th style={thStyle}>Fee Paid</th>
                <th style={thStyle} onClick={() => requestSort("joined")}>
                  Joined {getSortIcon("joined")}
                </th>
                <th style={thStyle} onClick={() => requestSort("createdBy")}>
                  Created By {getSortIcon("createdBy")}
                </th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr
                  key={client.id}
                  style={{
                    borderBottom: "1px solid #e5e7eb",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <td style={tdStyle}>{client.id}</td>
                  <td style={tdStyle}>{client.fullName}</td>
                  <td style={tdStyle}>{client.email}</td>
                  <td style={tdStyle}>
                    {client.currentBalance !== undefined ? (
                      <>${parseFloat(client.currentBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                    ) : (
                      <>N/A</>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: "0.25rem 0.75rem",
                        borderRadius: "999px",
                        backgroundColor:
                          client.status === "Active"
                            ? "#1e40af"
                            : client.status === "Inactive"
                            ? "#9ca3af"
                            : client.status === "Processing"
                            ? "#facc15"
                            : client.status === "Blocked"
                            ? "#ef4444"
                            : "#d1d5db",
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {client.status || "Unknown"}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <span
                      style={{
                        fontSize: "1.2rem",
                        color: client.feePaid ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {client.feePaid ? "✔" : "✘"}
                    </span>
                  </td>
                  <td style={tdStyle}>{client.joined || "N/A"}</td>
                  <td style={tdStyle}>{client.createdBy || "N/A"}</td>
                  <td style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      onClick={() => setSelectedClient(client)}
                      style={editBtnStyle}
                      title="Edit Client"
                    >
                      <FaUserEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      style={deleteBtnStyle}
                      title="Delete Client"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedClient && (
          <ManageClientForm
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
            onClientUpdated={(updatedClient) => {
              setClients((prev) =>
                prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
              );
              setSelectedClient(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: "0.75rem",
  fontSize: "0.8rem",
  color: "#374151",
  fontWeight: 600,
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const tdStyle = {
  padding: "0.25rem",
  fontSize: "0.8rem",
  color: "#374151",
  verticalAlign: "middle",
  maxWidth: "220px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "normal",
};

const editBtnStyle = {
  cursor: "pointer",
  backgroundColor: "#e0e7ff",
  border: "none",
  borderRadius: "6px",
  padding: "0.4rem 0.6rem",
  color: "#1e40af",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const deleteBtnStyle = {
  cursor: "pointer",
  backgroundColor: "#fee2e2",
  border: "none",
  borderRadius: "6px",
  padding: "0.4rem 0.6rem",
  color: "#991b1b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
